import { useCallback, useEffect, useMemo, useState } from 'react';
import * as R from 'remeda';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useRouteMatch } from 'react-router';
import { Link as RouterLink } from 'react-router-dom';
import { Queue } from '@rossum/api-client/queues';
import { QueueRule } from '@rossum/api-client/schemas';
import { ChipList } from '@rossum/components/chip-list/ChipList';
import { Filters } from '@rossum/components/filtering';
import { FilterChip } from '@rossum/components/filtering/components';
import SearchInput from '@rossum/components/search-input/SearchInput';
import { Tile, TileSkeleton } from '@rossum/components/tile';
import { IconLink } from '@rossum/ui/icons/tabler';
import { Box, Chip, Skeleton, Stack, Typography } from '@rossum/ui/material';
import { activitiesRulesPath } from '../../../containers/Activities/routes';
import { pushToDataLayer } from '../../../lib/pushToDataLayer';
import { isEmpoweredManager as isEmpoweredManagerSelector } from '../../../redux/modules/user/selectors';
import useDebounce from '../../../utils/hooks/useDebounce';
import { useStoredQuery } from '../../../utils/hooks/useStoredQuery';
import { useIsFeatureEnabled } from '../../feature-enablement/featureEnablement';
import { useWorkspacesWithQueues } from '../../queues/hooks/useWorkspacesWithQueues';
import { ruleManagerPath } from '../../rule-manager/routes';
import { MoveToAnotherQueueDialog } from '../components/MoveToAnotherQueueDialog';
import { getRuleContextMenuItems } from '../components/RuleContextMenu';
import {
  hasDeleteAction,
  useDeleteRuleWithUndo,
} from '../hooks/useDeleteRuleWithUndo';
import { useHandleDuplicateRule } from '../hooks/useHandleDuplicateRule';
import { useListRules } from '../hooks/useListRules';
import { hasMoveToAnotherQueueAction } from '../hooks/useMoveRule';
import { usePatchRule } from '../hooks/usePatchRule';
import { hasRemoveAction, useRemoveRule } from '../hooks/useRemoveRule';
import {
  rulesQueryParser,
  useRulesFilterSettings,
} from '../hooks/useRulesFilterSettings';
import { hasUnlinkAction, useUnlinkRule } from '../hooks/useUnlinkRule';
import { searchInRules } from '../searchUtils';
import { useActionOptions } from '../ThenSection/useActionOptions';
import { RulesPageEmptyState } from './EmptyState';

type RulesListProps = {
  queue?: Queue;
  onCreateRule: () => void;
};

export const RulesList = ({ queue, onCreateRule }: RulesListProps) => {
  const intl = useIntl();
  const { url } = useRouteMatch();
  const ruleManagerEnabled = useIsFeatureEnabled('rule-manager');
  const isEmpoweredManager = useSelector(isEmpoweredManagerSelector);
  const { actionOptions } = useActionOptions();

  const [moveQueueDialogOpen, setMoveQueueDialogOpen] =
    useState<QueueRule | null>(null);

  const {
    queues,
    workspaces,
    isLoading: isQueuesLoading,
  } = useWorkspacesWithQueues({
    enableQueries: true,
  });

  const [parsedParams, updateParsedParams] = useStoredQuery({
    parser: rulesQueryParser,
    localStorageKey: undefined,
  });

  const filterSettings = useRulesFilterSettings({
    queues,
    workspaces,
    isQueuesLoading,
    setFilters: updateParsedParams,
    canChangeQueue: !queue,
  });

  const { data: rules, isLoading } = useListRules({
    queue: queue?.id ?? parsedParams.queues,
    enabled: parsedParams.enabled,
    linked: parsedParams.linked,
    actions: parsedParams.action_type,
  });

  const [searchValue, setSearchValue] = useState<string>('');
  const debouncedSearchValue = useDebounce(searchValue, 300);

  useEffect(() => {
    if (debouncedSearchValue.length >= 3) {
      pushToDataLayer({
        event: 'rossumRulesSearch',
        searchValue: debouncedSearchValue,
      });
    }
  }, [debouncedSearchValue]);

  const {
    mutate: patchRule,
    isLoading: isPatching,
    variables: patchingVariables,
  } = usePatchRule();

  const filteredRules = useMemo(() => {
    return searchInRules(debouncedSearchValue, rules ?? []);
  }, [debouncedSearchValue, rules]);

  const hasRules = rules && rules.length > 0;

  const hasActiveFilters =
    parsedParams.queues !== undefined ||
    parsedParams.enabled !== undefined ||
    parsedParams.linked !== undefined ||
    parsedParams.action_type !== undefined ||
    debouncedSearchValue.length > 0;

  const displayPermanentQueueFilter = !queue && !parsedParams.queues;

  const queueFilterSetting = filterSettings.find(
    fs => fs.filterKey === 'queues'
  );

  const { mutate: handleDelete } = useDeleteRuleWithUndo();
  const { mutate: handleDuplicate } = useHandleDuplicateRule();
  const { mutate: handleRemove } = useRemoveRule();
  const { mutate: handleUnlink } = useUnlinkRule();

  const handleMoveToAnotherQueue = useCallback((rule: QueueRule) => {
    setMoveQueueDialogOpen(rule);
  }, []);

  // No rules at all and no filters active
  if (!isLoading && !hasRules && !hasActiveFilters) {
    return (
      <Stack flex={1} p={4}>
        <RulesPageEmptyState variant="empty" onCreateRule={onCreateRule} />
      </Stack>
    );
  }

  return (
    <Stack p={4} spacing={2}>
      {isQueuesLoading ? (
        <Stack
          direction="row"
          spacing={2}
          justifyContent="space-between"
          alignItems="center"
        >
          <Stack direction="row" spacing={2}>
            {[1, 2, 3].map(i => (
              <Skeleton key={i} variant="rounded" height={32} width={150} />
            ))}
          </Stack>
          <Skeleton variant="rounded" height={40} width={250} />
        </Stack>
      ) : (
        <Stack
          spacing={1}
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            justifyContent="flex-start"
          >
            {!!queueFilterSetting && displayPermanentQueueFilter ? (
              <FilterChip
                filterSetting={queueFilterSetting}
                filters={parsedParams}
                isValid={false}
                renderPlaceholderChip={({ onClick }) => (
                  <Chip
                    variant="filled"
                    label={intl.formatMessage({
                      id: 'features.queueSettings.rules.list.selectQueueFilterChip',
                    })}
                    onClick={onClick}
                  />
                )}
              />
            ) : null}
            <Filters
              dataCy="rules-filters"
              filters={parsedParams}
              setFilters={updateParsedParams}
              filterSettings={filterSettings}
            />
          </Stack>
          <SearchInput
            value={searchValue}
            onChange={value => setSearchValue(value)}
            placeholder={intl.formatMessage({
              id: 'features.queueSettings.rules.list.searchPlaceholder',
            })}
            sx={{ alignSelf: 'flex-end' }}
          />
        </Stack>
      )}
      {isLoading ? (
        <Stack spacing={0.5}>
          {[1, 2, 3].map(i => (
            <TileSkeleton
              key={i}
              skeletons={['title', 'description']}
              hasTo
              hasContextMenu
            />
          ))}
        </Stack>
      ) : filteredRules.length > 0 ? (
        <Stack spacing={0.5}>
          {filteredRules.map(rule => {
            const canRemove = hasRemoveAction(queue, rule);

            const canMoveToAnotherQueue = hasMoveToAnotherQueueAction(
              queue,
              rule
            );

            const canDelete = hasDeleteAction(queue, rule);

            const canUnlink = hasUnlinkAction(queue, rule);

            // Disable toggle when rule is linked to queues (not schema) and connected to more than 1 queue
            // Enable it in rule manager context (when queue is not defined)
            const isPatchingThisRule =
              isPatching && patchingVariables?.patchedRuleId === rule.id;
            const isToggleDisabled =
              isPatchingThisRule || (!!queue && rule.queues.length > 1);

            const contextMenuItems = getRuleContextMenuItems({
              intl,
              ruleManagerEnabled,
              onDuplicate: () => handleDuplicate({ rule, queue }),
              duplicateDisabled: isPatchingThisRule || isQueuesLoading,
              onMoveToAnotherQueue: canMoveToAnotherQueue
                ? () => handleMoveToAnotherQueue(rule)
                : undefined,
              onUnlink: canUnlink
                ? () => handleUnlink({ rule, queue })
                : undefined,
              // Empowered managers have no access to the activities page, so
              // don't offer the "Open activities" item for them.
              ruleExecutionLogsUrl: isEmpoweredManager
                ? undefined
                : `${activitiesRulesPath()}?rule=${rule.id}`,
              // Likewise, empowered managers can't reach the rule manager.
              ruleManagerPath:
                queue && !isEmpoweredManager
                  ? `${ruleManagerPath()}/${rule.id}/detail`
                  : undefined,
              onDelete: canDelete ? () => handleDelete({ rule }) : undefined,
              onRemove: canRemove
                ? () => handleRemove({ rule, queue })
                : undefined,
            });

            const endChips = rule.actions
              .map(action => {
                const knownAction = actionOptions.find(a =>
                  a.resolveAction(action)
                );
                if (!knownAction?.icon) return null;
                return {
                  key: String(action.id),
                  label: '',
                  Icon: knownAction.icon,
                  tooltip: knownAction.name,
                };
              })
              .filter(R.isNonNullish);

            return (
              <Tile
                key={rule.id}
                data-cy="rule-tile"
                data-id={`${rule.id}`}
                title={rule.name}
                inlineBadges={
                  ruleManagerEnabled &&
                  !isEmpoweredManager &&
                  rule.queues.length > 1 &&
                  !!queue
                    ? [
                        {
                          key: 'linked-queues',
                          label: String(rule.queues.length),
                          Icon: IconLink,
                          to: `${ruleManagerPath()}/${rule.id}/queues`,
                        },
                      ]
                    : undefined
                }
                description={
                  queue ? (
                    rule.description
                  ) : (
                    <Stack spacing={0.5}>
                      {rule.description && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          noWrap
                        >
                          {rule.description}
                        </Typography>
                      )}
                      <Box
                        component={RouterLink}
                        to={`${ruleManagerPath()}/${rule.id}/queues`}
                        onClick={e => e.stopPropagation()}
                        sx={{
                          width: 'fit-content',
                          textDecoration: 'none !important',
                          color: 'inherit',
                          '&:hover .MuiChip-root': {
                            backgroundColor: t => t.palette.action.selected,
                          },
                        }}
                      >
                        <ChipList
                          chips={
                            queues
                              ?.filter(q => R.isIncludedIn(q.url, rule.queues))
                              .map(q => {
                                const workspace = workspaces?.find(
                                  w => w.url === q.workspace
                                );
                                return {
                                  id: q.id,
                                  primary: workspace?.name ?? '',
                                  secondary: q.name,
                                };
                              }) ?? []
                          }
                          isLoading={isQueuesLoading}
                          emptyMessage={intl.formatMessage({
                            id: 'features.rules.rulesList.noQueues',
                          })}
                          visibleChipsCount={2}
                        />
                      </Box>
                    </Stack>
                  )
                }
                endChips={endChips}
                disabled={!rule.enabled}
                toggleAction={{
                  checked: rule.enabled,
                  disabled: isToggleDisabled,
                  onChange: () =>
                    patchRule({
                      patchedRuleId: rule.id,
                      patchedRule: { ...rule, enabled: !rule.enabled },
                    }),
                }}
                contextMenuItems={contextMenuItems}
                to={`${url}/${rule.id}/detail`}
                dragHandle={null}
              />
            );
          })}
        </Stack>
      ) : hasRules ? (
        <Stack height="50vh">
          <RulesPageEmptyState
            variant="noResults"
            searchValue={debouncedSearchValue}
            onClearSearch={() => setSearchValue('')}
          />
        </Stack>
      ) : (
        <Stack height="50vh">
          <RulesPageEmptyState
            variant="noFilterResults"
            onClearFilters={() =>
              updateParsedParams({
                queues: undefined,
                enabled: undefined,
                linked: undefined,
                action_type: undefined,
              })
            }
          />
        </Stack>
      )}
      <MoveToAnotherQueueDialog
        key={moveQueueDialogOpen === null ? 'close' : 'open'}
        open={moveQueueDialogOpen != null}
        onClose={() => setMoveQueueDialogOpen(null)}
        rule={moveQueueDialogOpen}
      />
    </Stack>
  );
};
